import { lazy, Suspense, useEffect } from "react";
import { useOktaAuth } from "@okta/okta-react";
import FileActions from "../../services/utils/FileActions";
import Button from "../organism/Button";
import { useContext, useState, Fragment, useCallback } from "react";
import toastMessages from "../../services/utils/helpers/toastMessage";
import { useHistory } from "react-router-dom";
import ReportsPopup from "../popups/reportsPopup";
import { PopUpContext } from "../../services/context/popupContext";
import PDFPrint from "../global/PDFPrint";
import useCancelPickup from "../../services/utils/custom hooks/Shipments/useCancelPickup";
import useDisassociateLabel from "../../services/utils/custom hooks/Shipments/useDisassociateLabel";
import useFeatureFlags from "../../services/utils/custom hooks/Feature Flagging/useFeatureFlags";
import dataConstants from "../../constants/dataConstants";
import ButtonProps from "../../services/models/buttonProps.model";
import UploadDocumentsPopup from "../popups/uploadDocumentsPopup";
import PrintSvg from "../../assets/svg/PrintSvg";
import DownloadFileSvg from "../../assets/svg/DownloadFileSvg";
import { selectedRowsState } from "../../services/context/selectedRowsIDContext";

function ActionsCell({
  row,
}: {
  cell: any;
  row: any;
  table: any;
}): JSX.Element {
  const fileActions = new FileActions();
  const { authState } = useOktaAuth();
  const [downloadingReport, setDownloadingReport] = useState<boolean>(false);
  const [fetchingReports, setFetchingReports] = useState<boolean>(false);
  const { getActionsForSection } = useFeatureFlags();
  const { visible, setVisible, setPopUp, setPopUpTitle, setOnlineOrder } =
    useContext(PopUpContext);
  const [cancelling, setCancelling] = useState<boolean>(false);
  const [removing, setRemoving] = useState<boolean>(false);
  const history = useHistory();
  const [printId, setPrintId] = useState<string[]>([]);
  const [printing, setPrinting] = useState<boolean>(false);
  const { state, dispatch: dispatchRows  } = useContext(selectedRowsState);
  const { approvalPopup } = state;

  const togglePopup = () => {
    dispatchRows({ type: "SET_APPROVAL_POPUP", payload: !approvalPopup });
    dispatchRows({ type: "SET_SELECTED_ROWS_IDS", payload: row });
  };


  async function handleReportDownload(e: any) {
    e.stopPropagation();
    const id = row.original.id;
    const fileName =
      row.original.patientFirstName + " " + row.original.patientLastName;

    setDownloadingReport(true);
    try {
      const response = await fileActions.downloadReport({
        id,
        token: authState?.accessToken?.accessToken ?? "",
        fileName,
      });
      setDownloadingReport(false);
    } catch (error: any) {
      setDownloadingReport(false);
      toastMessages.error(
        error?.response?.data?.message ??
          error.message ??
          "Something went wrong"
      );
    }
  }
  async function handleOrderDownload() {
    let testName = !!row.original?.testTypes?.name
      ? row.original?.testTypes?.name.split(":")[0]
      : "";
    let fileName = `${row.original?.id}_${
      !!row.original?.patientName ? `${row.original?.patientName} ` : "Patient"
    }_${testName}`;

    setDownloadingReport(true);
    try {
      const response = await fileActions.downloadMultiplePDF(
        [{ id: row.original?.id, fileName }],
        authState?.accessToken?.accessToken ?? ""
      );
      setDownloadingReport(false);
    } catch (error: any) {
      setDownloadingReport(false);
      toastMessages.error(
        error?.response?.data?.message ??
          error.message ??
          "Something went wrong"
      );
      setDownloadingReport(false);
    }
    setDownloadingReport(false);
  }
  async function handleViewReports(e: any) {
    e.stopPropagation();
    const id = row.original.id;

    setFetchingReports(true);
    try {
      const response = await fileActions.viewReports({
        id,
        token: authState?.accessToken?.accessToken ?? "",
      });

      setFetchingReports(false);

      if (response.length > 0) {
        setVisible(!visible);
        setPopUpTitle("Test Reports");
        setPopUp(<ReportsPopup reports={response} />);
      }
    } catch (error: any) {
      setFetchingReports(false);
      toastMessages.error(
        error?.response?.data?.message ??
          error.message ??
          "Something went wrong"
      );
    }
  }
  function handleUploadDocuments() {
    setVisible(!visible);
    setPopUpTitle("Upload and transmit more documents");
    setPopUp(
      <UploadDocumentsPopup
        authState={authState}
        orderId={row.original.id}
        closePopup={() => setVisible(false)}
      />
    );
  }
  const { mutate: cancelPickup } = useCancelPickup(setCancelling);
  const { mutate: disassociateLabel } = useDisassociateLabel(setRemoving, row);
  const orderButtons: ButtonProps[] = [
    {
      textInformation: {
        text: "Download Report",
        fontWeight: "font-semibold",
        fontSize: "text-xs sm:text-sm",
      },
      loading: downloadingReport,
      disabled:
        downloadingReport ||
        (row?.original?.orderStatus?.toLowerCase?.() !== "completed" &&
          row?.original?.orderReport?.length === 0),
      onClick: handleReportDownload,
      buttonTheme: "secondary",
      className: " my-1",
      key: dataConstants.SectionActionKeys.DOWNLOAD_REPORT,
    },
    {
      textInformation: {
        text: "View Report",
        fontWeight: "font-semibold",
        fontSize: "text-xs sm:text-sm",
      },
      loading: fetchingReports,
      disabled:
        fetchingReports ||
        row?.original?.orderStatus?.toLowerCase?.() !== "completed",
      onClick: handleViewReports,
      buttonTheme: "secondary",
      className: "mx-5 my-1",
      key: dataConstants.SectionActionKeys.VIEW_ORDER_REPORT,
    },
    {
      textInformation: {
        text: "Detail",
        fontWeight: "font-semibold",
        fontSize: "text-xs sm:text-sm",
      },
      onClick: (e) => {
        e.stopPropagation();
        history.replace(`/access/test-orders/summary/${row?.original?.id}`);
      },
      buttonTheme: "secondary",
      className: "mx-5 my-1",
      key: dataConstants.SectionActionKeys.VIEW_ORDER_DETAIL,
    },
  ];
  const documentButtons: ButtonProps[] = [
    {
      textInformation: {
        text: "Upload Documents",
        fontWeight: "font-semibold",
        fontSize: "text-xs sm:text-sm",
      },
      loading: false,
      disabled: false,
      onClick: handleUploadDocuments,
      buttonTheme: "secondary",
      className: "my-1",
      key: dataConstants.SectionActionKeys.UPLOAD_DOCUMENTS,
    },
    {
      textInformation: {
        text: "Detail",
        fontWeight: "font-semibold",
        fontSize: "text-xs sm:text-sm",
      },
      onClick: (e) => {
        e.stopPropagation();
        history.replace(`/access/test-orders/summary/${row?.original?.id}`);
      },
      buttonTheme: "secondary",
      className: "mx-5 my-1",
      key: dataConstants.SectionActionKeys.VIEW_ORDER_DETAIL,
    },
  ];
  const approvalButtons: ButtonProps[] = [
    {
      textInformation: {
        // text: `${printing ? "Printing" : "print"}`,
        text: <PrintSvg fill={"#172B4D"} />,
        fontWeight: "font-thin",
        fontSize: "text-xs sm:text-sm",
      },
      loading: printing,
      disabled: printing,
      onClick: PrintOrder,
      buttonTheme: "secondary",
      className: " my-1",
      key: dataConstants.SectionActionKeys.PRINT_ORDER,
    },
    {
      textInformation: {
        // text: "Download",
        text: <DownloadFileSvg fill={"#172B4D"} />,
        fontWeight: "font-semibold",
        fontSize: "text-xs sm:text-sm",
      },
      loading: downloadingReport,
      disabled: downloadingReport,
      onClick: handleOrderDownload,
      buttonTheme: "secondary",
      className: "ml-3 my-1",
      key: dataConstants.SectionActionKeys.DOWNLOAD_ORDER,
    },
    {
      textInformation: {
        text: "Authorize",
        fontWeight: "font-semibold",
        fontSize: "text-xs sm:text-sm",
      },
      onClick: ()=> togglePopup(),
      buttonTheme: "secondary",
      className: "ml-3 my-1",
      key: dataConstants.SectionActionKeys.AUTHORIZE_ORDER,
    },
    {
      textInformation: {
        text: "Review",
        fontWeight: "font-semibold",
        fontSize: "text-xs sm:text-sm",
      },
      onClick: (e) => {
        history.push(`/access/approvals/review?ids=${row.original.id}`);
      },
      buttonTheme: "secondary",
      className: "ml-3 my-1",
      key: dataConstants.SectionActionKeys.AUTHORIZE_ORDER,
    },
  ];

  function PrintOrder() {
    if (!!row?.original?.id) setPrintId([row?.original?.id]);
  }

  // Memoize callbacks to prevent infinite loops in PDFPrint
  const handlePrintStart = useCallback(() => {
    setPrinting(true);
  }, []);

  const handlePrintError = useCallback((message: string) => {
    toastMessages.error(message);
    setPrinting(false);
  }, []);

  const handlePrintSuccess = useCallback(() => {
    setPrinting(false);
    setPrintId([]); // Clear printId after successful print
  }, []);

  if (history.location.pathname?.includes("approval")) {
    return (
      <Fragment>
        <PDFPrint
          onStart={handlePrintStart}
          onError={handlePrintError}
          onSuccess={handlePrintSuccess}
          ids={printId}
        />
        <div className="flex ">
          {approvalButtons
            .filter(
              (button) =>
                getActionsForSection(
                  dataConstants.FeatureFlagSectionKeys.APPROVALS
                ).find((action) => action.key === button.key)?.isActive
            )
            .map((button) => (
              <Button {...button} />
            ))}
        </div>
      </Fragment>
    );
  }

  if (history.location?.pathname?.includes("pick-ups")) {
    return (
      <Button
        textInformation={{
          text: cancelling ? "Cancelling pick-up" : "Cancel pick-up",
          fontWeight: "font-semibold",
        }}
        loading={cancelling}
        disabled={row?.original?.status !== "PICKUP_SCHEDULED" || cancelling}
        onClick={() => cancelPickup(row.original.id)}
        buttonTheme="transparent"
        className=" my-1 text-[#18b5b7]"
      />
    );
  }

  if (history?.location?.pathname?.includes("active-labels")) {
    return (
      <Button
        textInformation={{
          text: removing ? "removing" : "remove",
          fontWeight: "font-semibold",
        }}
        loading={removing}
        disabled={removing}
        onClick={(e) => {
          e.stopPropagation();
          disassociateLabel(row?.formattedData);
        }}
        buttonTheme="transparent"
        className="my-1 text-[#18b5b7]"
      />
    );
  }

  if (history?.location?.pathname?.includes("documents"))
    return (
      <div className="overflow-y-auto flex items-center">
        {documentButtons
          /* .filter(
            (button) =>
              getActionsForSection(
                dataConstants.FeatureFlagSectionKeys.DOCUMENTS
              ).find((action) => action.key === button.key)?.isActive
          ) */
          .map((button) => (
            <Button {...button} />
          ))}
      </div>
    );

  return (
    <div className="overflow-y-auto flex items-center">
      {orderButtons
        .filter(
          (button) =>
            getActionsForSection(
              history?.location?.pathname?.includes("documents")
                ? dataConstants.FeatureFlagSectionKeys.DOCUMENTS
                : dataConstants.FeatureFlagSectionKeys.TEST_ORDERS
            ).find((action) => action.key === button.key)?.isActive
        )
        .map((button) => (
          <Button {...button} />
        ))}
    </div>
  );
}

export default ActionsCell;
